import $ from 'jquery'
import Overlay from 'ol/Overlay'
import Point from 'ol/geom/Point'
import { Text as OLText, Fill, Stroke, Style } from 'ol/style'
import Feature from 'ol/Feature'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Tool from '../Tool'
import Text from '../interaction/Text'

import { Pixel } from 'ol/pixel'
import Layer from 'ol/layer/Layer'
import { Coordinate } from 'ol/coordinate'

/**
 * Text tool class
 *
 * @param {Object} config
 * @constructor
 */
export default class Deselect extends Tool {
  constructor(config) {
    super()

    $.extend(this, config)

    this.ui.iconClass = 'map-text'

    this.$popover = $('#text-tool-popover')
    this.$buttonSubmit = this.$popover.find('#edit-text-submit')
    this.$buttonDelete = this.$popover.find('#edit-text-delete')
    this.$buttonClose = this.$popover.find('.popover-close')
    this.$inputText = this.$popover.find('#edit-text')

    this.events = []

    this.selectedLayer = null

    var popupOffset = this.popoverGetOffset()

    this.popoverOverlay = new Overlay({
      element: this.$popover.get(0),
      offset: [popupOffset.x, popupOffset.y]
    })

    this.map.addOverlay(this.popoverOverlay)

    this.editOnly = false

    this.isActivated = false
  }

  /**
   * Activate text tool
   */
  activate() {
    if (this.isActivated && this.editOnly) {
      this.deactivate()
    }

    this.interaction = new Text({
      text: this
    })

    this.events = [
      this.$buttonClose.on('click', this.handleButtonClose.bind(this)),
      this.$buttonDelete.on('click', this.handleButtonDelete.bind(this)),
      this.$buttonSubmit.on(
        'click touchstart',
        this.handleButtonSubmit.bind(this)
      ),
      this.$inputText.on('keypress', this.handleInputKeypress.bind(this)),
      this.$inputText.on('blur', this.handleInputBlur.bind(this))
    ]

    this.isActivated = true
    super.activate()
  }

  /**
   * Deactivate text tool
   */
  deactivate() {
    this.events.forEach(e => e.off())

    this.popoverHide()

    this.events = []
    this.map.removeInteraction(this.interaction)
    this.isActivated = false
    super.deactivate()
    this.editOnly = false
  }
  /*
   * Handles map click event
   */
  handleMapClick(e) {
    var coordinate
    var layer = this.getLayerFromPixel(e.pixel)

    if (this.isTextLayer(layer)) {
      if (!this.activated) {
        this.editOnly = true
        this.activate()
      }
      this.selectedLayer = layer
      this.loadTextFromLayer(layer)
      coordinate = this.textGetGeometry(layer)
      this.popoverSetPosition(coordinate)
      this.popoverShow()
    } else if (this.activated && !this.editOnly) {
      coordinate = e.coordinate
      this.popoverSetPosition(coordinate)
      this.popoverShow()
    } else {
      this.popoverHide()
    }
  }
  /**
   * Handles input field keypress event
   */
  handleInputKeypress(e) {
    var enterKey = 13
    if (e.which === enterKey) {
      this.textSave()
    }
  }
  /**
   * Handles input field blur event
   */
  handleInputBlur() {
    // Hack that prevents fixed navbars from getting wrong position after using keyboard in iOS
    $(window).scrollTop(0)
  }
  /**
   * Handles submit button click event
   */
  handleButtonSubmit(e) {
    e.preventDefault()
    this.textSave()
  }
  /**
   * Handles delete button click event
   */
  handleButtonDelete() {
    this.textRemoveSelected()
    this.popoverHide()
  }
  /**
   * Handles close button click event
   */
  handleButtonClose() {
    this.popoverHide()
    this.selectedLayer = null
  }
  /**
   * Get first layer from selected pixel
   * @param {Pixel} pixel
   * @return {Layer}
   */
  getLayerFromPixel(pixel) {
    var layer = this.map.forEachFeatureAtPixel(pixel, function(feature, layer) {
      return layer
    })
    return layer
  }
  /**
   * Reset text input
   */
  inputReset() {
    this.$inputText.val('')
  }
  /**
   * Checks if layer is a text tool layer
   * @param {Layer} layer
   * @return {bool}
   */
  isTextLayer(layer) {
    var olgaProperties = null
    if (layer) {
      olgaProperties = layer.get('olga_')
    }
    return (
      olgaProperties && olgaProperties.type && olgaProperties.type === 'Text'
    )
  }
  /**
   * Sets popover position
   * @param {Coordinate} coordinate
   */
  popoverSetPosition(coordinate) {
    this.popoverOverlay.setPosition(coordinate)
  }
  /**
   * Gets popover offset based on popover element size
   * @return {Object}
   */
  popoverGetOffset() {
    var offset = {
      x: this.$popover.width() / -2,
      y: this.$popover.height() * -1
    }
    return offset
  }
  /**
   * Shows popover
   */
  popoverShow() {
    this.$popover.show()
  }
  /**
   * Hides popover
   */
  popoverHide() {
    this.$popover.hide()
    this.inputReset()
  }
  /**
   * Adds or updates texts
   */
  textSave() {
    var text = this.$inputText.val()

    var coordinate = this.popoverOverlay.get('position')
    var point = new Point(coordinate)

    this.textRemoveSelected()
    this.popoverHide()

    if (text.length > 0) {
      this.textAdd(text, point)
    }
  }
  /**
   * Add a new text to map
   * @param {String} text
   * @param {Point} point
   */
  textAdd(text, point) {
    var layer = this.createTextLayer(text, point)
    this.map.addLayer(layer)
  }
  /**
   * Removes selected text layer from map
   */
  textRemoveSelected() {
    this.map.removeLayer(this.selectedLayer)
    this.selectedLayer = null
  }
  /**
   * Gets geometry from layer
   * @param {Layer} layer
   * @return {Coordinate}
   */
  textGetGeometry(layer) {
    var coordinate
    var source = layer.getSource()
    coordinate = source.forEachFeature(function(feature) {
      var cors = feature.get('geometry')
      return cors.flatCoordinates
    })

    return coordinate
  }
  /**
   * Loads text from layer into popover input field
   * @param {Layer} layer
   */
  loadTextFromLayer(layer) {
    var text = ''
    var olgaProperties
    if (layer) {
      olgaProperties = layer.get('olga_')
    }
    if (olgaProperties) {
      text = olgaProperties.text
    }

    this.$inputText.val(text)
  }
  /**
   * Creates a text style
   * @param {String} text
   * @return {Style}
   */
  createTextStyle(text) {
    var style = new Style({
      text: new OLText({
        font: 'normal 16px Helvetica',
        text: text,
        size: 16,
        offsetX: 0,
        offsetY: 0,
        fill: new Fill({
          color: 'rgba(0, 0, 0, 1)' // Color
        }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.6)', // White
          width: 2
        }),
        scale: 1
      })
    })

    return style
  }
  /**
   * Creates a text layer
   * @param {Point} point
   * @return {Layer}
   */
  createTextLayer(text, point) {
    var style = this.createTextStyle(text)

    var feature = new Feature({
      geometry: point // new ol.geom.Point([2006032.8437532112, 8252329.640117822]),
    })

    var source = new VectorSource({
      features: [feature]
    })

    var layer = new VectorLayer({
      source: source,
      style: style,
      olga_: {
        type: 'Text',
        text: text
      }
    })

    return layer
  }
}
